import SortIcon from "@/core/ui/iconsax/linear/arrow-3.svg"
import { OrderByInput } from "@/product/reports/table/__generated__/ProgressReportTablePaginationQuery.graphql"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import useShowOnHoverStyles from "@assets/style/util/useShowOnHoverStyles"
import {
  DiscoButton,
  DiscoDropdown,
  DiscoIcon,
  DiscoIconButton,
  DiscoIconKinds,
  DiscoText,
  DiscoTextSkeleton,
  DiscoTooltip,
  MenuButtonProps,
  SelectOption,
} from "@disco-ui"
import { OverridableDiscoButton } from "@disco-ui/button/OverridableDiscoButton"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DropdownIcon from "@disco-ui/dropdown/DropdownIcon"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"

export const SORT_DIRECTION_LABEL: Record<DiscoTableSortDirection, string> = {
  ASC: "Ascending",
  DESC: "Descending",
}

export type DiscoTableSortDirection = "ASC" | "DESC"

interface DiscoTableSortDropdownProps extends TestIDProps {
  options: DiscoTableSortOption[]
  onOptionSelect: (opt: DiscoTableSortOption, direction: DiscoTableSortDirection) => void
  toolbarState: DiscoTableToolbarStateWithSort
  variant?: "default" | "icon"
  className?: string
}

export type DiscoTableToolbarState = {
  search: string
}

export type DiscoTableToolbarStateWithSort = {
  search?: string
  sort: {
    id: string
    order: { field: string }
    title: string
  }
  direction: DiscoTableSortDirection
  viewType?: string
}

export type DiscoTableSortOption = SelectOption<
  string,
  {
    order: Omit<OrderByInput, "direction">
    icon?: React.ReactNode
    onlyDirection?: DiscoTableSortDirection
  }
>

function DiscoTableSortDropdown({
  testid,
  options,
  onOptionSelect,
  toolbarState,
  variant = "default",
  className: customClassName,
}: DiscoTableSortDropdownProps) {
  const classes = useStyles()
  const showOnHoverClasses = useShowOnHoverStyles()

  return (
    <DiscoDropdown
      testid={`${testid}.sort-select`}
      menuClasses={{
        paper: classes.menu,
      }}
      menuButton={(btnProps) => renderButton(btnProps)}
      hideCarat={variant === "icon"}
    >
      <DiscoText
        variant={"body-xs-500"}
        marginTop={0.5}
        marginBottom={0.5}
        color={"groovy.grey.400"}
      >
        {"Sort by..."}
      </DiscoText>

      {/* Sort Options */}
      {options.map((option, index) => renderOptionItem(option, index))}
    </DiscoDropdown>
  )

  function renderButton(props: MenuButtonProps) {
    switch (variant) {
      case "icon":
        return (
          <DiscoTooltip
            content={`${toolbarState.sort.title} - ${
              SORT_DIRECTION_LABEL[toolbarState.direction]
            }`}
          >
            <OverridableDiscoButton
              {...props}
              width={"40px"}
              classes={{ root: classes.dropdownIconButton }}
              data-testid={`${testid}.sort-select.button`}
            >
              <DiscoIcon icon={"sort"} rotate={"90"} />
            </OverridableDiscoButton>
          </DiscoTooltip>
        )
      case "default":
      default:
        return (
          <DiscoButton
            {...props}
            testid={`${testid}.sort-select.button`}
            className={classNames(classes.dropdownButton, customClassName)}
          >
            {toolbarState.sort.title}
          </DiscoButton>
        )
    }
  }

  function renderOptionItem(option: DiscoTableSortOption, index: number) {
    const isSelected = toolbarState.sort.id === option.value
    const direction =
      option.context?.onlyDirection ||
      (isSelected
        ? // If the option is selected, toggle the direction
          toolbarState.direction === "ASC"
          ? "DESC"
          : "ASC"
        : // Otherwise, use descending
          "DESC")

    return (
      <DiscoDropdownItem
        key={option.value}
        title={option.title}
        icon={option.context!.icon as DiscoIconKinds}
        onClick={() => onOptionSelect(option, direction)}
        testid={`${testid}.sort-select.item-${index}`}
        className={showOnHoverClasses.hoverable}
        rightButton={
          <DiscoIconButton
            classes={{
              root: classNames(classes.sortButton, showOnHoverClasses.showable),
            }}
            svgStyles={{ height: 16, width: 16 }}
          >
            <SortIcon />
          </DiscoIconButton>
        }
        tooltip={direction === "ASC" ? "Sort Ascending" : "Sort Descending"}
      />
    )
  }
}

const useStyles = makeUseStyles((theme) => ({
  dropdownButton: {
    minWidth: "150px",
    justifyContent: "space-between",
  },
  dropdownIconButton: {
    padding: theme.spacing(1),
  },
  menu: {
    minWidth: "180px",
  },
  sortButton: {
    padding: 0,
    paddingLeft: theme.spacing(1),
    "& span > svg": {
      // The sort icon should be groovy.grey.400 to match the other icon hover state
      // Use important! to override the default hover color in DiscoDropdownItem
      color: `${theme.palette.groovy.grey[400]} !important`,
    },
  },
}))

type DiscoTableSortDropdownSkeletonProps = {
  width?: string
}

export const DiscoTableSortDropdownSkeleton = ({
  width,
  variant = "default",
}: DiscoTableSortDropdownSkeletonProps & { variant?: "default" | "icon" }) => {
  const classes = useStyles()

  switch (variant) {
    case "icon":
      return (
        <OverridableDiscoButton
          width={"40px"}
          classes={{ root: classes.dropdownIconButton }}
          variant={"outlined"}
          color={"grey"}
        >
          <DiscoIcon icon={"sort"} rotate={"90"} />
        </OverridableDiscoButton>
      )
    case "default":
    default:
      return (
        <DiscoButton
          rightIcon={<DropdownIcon />}
          color={"grey"}
          variant={"outlined"}
          className={classes.dropdownButton}
        >
          <DiscoTextSkeleton width={width || 75} />
        </DiscoButton>
      )
  }
}

export default DiscoTableSortDropdown
